import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import SidebarLayout from "components/ui/SidebarLayout";
import LoginAndAuthentication from './pages/login-and-authentication';
import EmployeeManagement from './pages/employee-management';

import ProductManagementSimple from './pages/product-management-simple';
import PieceRegistration from './pages/piece-registration';
import LoncheadosControlCenter from './pages/loncheados-control-center';
import InformesLoncheado from './pages/informes-loncheado';
import ExecutiveDashboard from './pages/executive-dashboard';
import ClientProfitabilityAnalysis from './pages/client-profitability-analysis';
import FinancialAnalysisDashboard from './pages/financial-analysis-dashboard';
import Salarios from './pages/salarios';
import CalendarioEventos from './pages/calendario-eventos';
import DashboardGeneral from './pages/dashboard-general';
// New business unit pages
import TiendaGeneral from './pages/tienda-general';
import TiendaInventario from './pages/tienda-inventario';
import Pedidos from './pages/tienda-pedidos-web';
import NuevoPedido from './pages/pedidos-nuevo';
import PedidoDetalle from './pages/pedidos-detalle';
import PedidosDashboard from './pages/pedidos-dashboard';
import PedidosOperativa from './pages/pedidos-operativa';
import PedidosIncidencias from './pages/pedidos-incidencias';
import PedidosHistorico from './pages/pedidos-historico';
import PedidosCanales from './pages/pedidos-canales';

import TiendaVentas from './pages/tienda-ventas';
import Distribucion from './pages/distribucion';
import FinanzasCorporativas from './pages/finanzas-corporativas';
import IAEmpresarial from './pages/ia-empresarial';
// Distribución submódulos
import DistInventario from './pages/dist-inventario';
import DistClientes from './pages/dist-clientes';
import ClienteB2B from './pages/dist-clientes/ClienteB2B';
import DistTarifas from './pages/dist-tarifas';
import DistDocumentos from './pages/dist-documentos';
import DistProductos from './pages/dist-productos';
import DistInventarioB2B from './pages/dist-inventario-b2b';
import DistPedidos from './pages/dist-pedidos';
import DistNuevoPedido from './pages/dist-nuevo-pedido';
import DistAnalitica from './pages/dist-analitica';
import DistFacturasEntrantes from './pages/dist-facturas-entrantes';
import DistFacturasSalientes from './pages/dist-facturas-salientes';
// Finanzas Corporativas submódulos
import FinFacturacion from './pages/fin-facturacion';
import FinContabilidad from './pages/fin-contabilidad';
import FinTesoreria from './pages/fin-tesoreria';
import FinBancos from './pages/fin-bancos';
import FinGastos from './pages/fin-gastos';
import FinConfiguracionEmpresa from './pages/fin-configuracion-empresa';
// New document capture & pricing modules
import TiendaFinanzas from './pages/tienda-finanzas';
import FinAlbaranes from './pages/fin-albaranes';
import CostesPricing from './pages/costes-pricing';
import LectorPrecios from './pages/lector-precios';
import AuditoriaTotal from './pages/auditoria-total';
// Tienda General new pages
import TiendaProductos from './pages/tienda-productos';
import GestionPrecios from './pages/precios';
import TiendaCompras from './pages/tienda-compras';
import TiendaCajaDiaria from './pages/tienda-caja-diaria';
import TiendaDashboardFinanciero from './pages/tienda-dashboard-financiero';
import TiendaMovimientos from './pages/tienda-movimientos';
import TiendaProveedores from './pages/tienda-proveedores';
import TiendaAnalitica from './pages/tienda-analitica';
import APPCCAutocontrol from './pages/tienda-appcc';
// Unified Clients
import ClientesUnificados from './pages/clientes';
import ClientDetail from './pages/clientes/ClientDetail';
// Legacy ERP stubs (kept for backward compat redirects)
import ERPInventario from './pages/erp-inventario';
import ERPComercial from './pages/erp-comercial';
import Trazabilidad from './pages/trazabilidad';

// Comercial module
import ComercialDashboard from './pages/comercial-dashboard';
import ComercialClientes from './pages/comercial-clientes';
import ComercialClienteFicha from './pages/comercial-clientes/ClienteFicha';
import ComercialComunicaciones from './pages/comercial-comunicaciones';
import ComercialAtencion from './pages/comercial-atencion';
import ComercialCampanas from './pages/comercial-campanas';
import ComercialMarketing from './pages/comercial-marketing';

import PrivacyPolicy from './pages/privacy-policy';
import PlanTrabajo from './pages/plan-trabajo';
import Configuracion from './pages/configuracion';
import { useAuth } from './contexts/AuthContext';

// Corporate loading screen shown while verifying session
const LoadingScreen = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 40%, #0D0D0D 70%, #0A0A0A 100%)' }}
  >
    <div className="flex flex-col items-center gap-6">
      <div
        className="p-4 rounded-2xl"
        style={{ background: 'rgba(214,239,78,0.06)', border: '1px solid rgba(214,239,78,0.15)' }}
      >
        <img
          src="/assets/images/logo_las_dos_doncellas_1MB-1784658867185.jpg"
          alt="Las Dos Doncellas"
          className="w-24 h-24 object-contain rounded-xl"
        />
      </div>
      <div className="text-center">
        <h1 className="font-oranienbaum font-bold text-white text-2xl tracking-wide mb-1">
          LAS DOS DONCELLAS
        </h1>
        <p className="font-oranienbaum text-[#D6EF4E] text-sm tracking-widest uppercase">
          ERP Empresarial
        </p>
      </div>
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin"
        />
        <p className="font-oranienbaum text-white/50 text-sm tracking-wide">
          Verificando acceso...
        </p>
      </div>
    </div>
  </div>
);

// Protects all internal routes — redirects to /login if not authenticated
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? (
    <SidebarLayout>{children}</SidebarLayout>
  ) : (
    <Navigate to="/login" replace />
  );
};

// Redirects authenticated users away from /login to dashboard
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? <Navigate to="/dashboard-general" replace /> : children;
};

// Cloudflare Pages: rutas de ventas Epelsa unificadas.
const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><LoginAndAuthentication /></PublicRoute>} />
          <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />


          {/* Root redirect */}
          <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard-general" replace /></ProtectedRoute>} />

          {/* ── CONFIGURACIÓN ── */}
          <Route path="/configuracion" element={<ProtectedRoute><Configuracion /></ProtectedRoute>} />

          {/* ── 1. Dashboard General ── */}
          <Route path="/dashboard-general" element={<ProtectedRoute><DashboardGeneral /></ProtectedRoute>} />

          {/* ── 2. SALA DE CORTE (intact) ── */}
          <Route path="/dashboard-sala-corte" element={<ProtectedRoute><ExecutiveDashboard /></ProtectedRoute>} />
          <Route path="/executive-dashboard" element={<ProtectedRoute><Navigate to="/dashboard-sala-corte" replace /></ProtectedRoute>} />
          <Route path="/plan-trabajo" element={<ProtectedRoute><PlanTrabajo /></ProtectedRoute>} />
          <Route path="/piece-registration-form" element={<ProtectedRoute><PieceRegistration /></ProtectedRoute>} />
          <Route path="/piece-registration" element={<ProtectedRoute><PieceRegistration /></ProtectedRoute>} />
          <Route path="/loncheados-control-center" element={<ProtectedRoute><LoncheadosControlCenter /></ProtectedRoute>} />
          <Route path="/informes-loncheado" element={<ProtectedRoute><InformesLoncheado /></ProtectedRoute>} />
          {/* Catálogo de Piezas — visual rename of product-management-simple (uses public.productos) */}
          <Route path="/product-management-simple" element={<ProtectedRoute><ProductManagementSimple /></ProtectedRoute>} />
          <Route path="/catalogo-piezas" element={<ProtectedRoute><Navigate to="/product-management-simple" replace /></ProtectedRoute>} />
          {/* Clientes Sala — visual rename of /clientes */}
          <Route path="/client-management" element={<ProtectedRoute><Navigate to="/clientes" replace /></ProtectedRoute>} />
          {/* Operarios — visual rename of employee-management */}
          <Route path="/employee-management" element={<ProtectedRoute><EmployeeManagement /></ProtectedRoute>} />
          <Route path="/operarios" element={<ProtectedRoute><Navigate to="/employee-management" replace /></ProtectedRoute>} />
          {/* Salarios de Corte — visual rename of /salarios */}
          <Route path="/salarios" element={<ProtectedRoute><Salarios /></ProtectedRoute>} />
          <Route path="/salarios-corte" element={<ProtectedRoute><Navigate to="/salarios" replace /></ProtectedRoute>} />
          {/* Rentabilidad Sala — visual rename of client-profitability-analysis */}
          <Route path="/client-profitability-analysis" element={<ProtectedRoute><ClientProfitabilityAnalysis /></ProtectedRoute>} />
          <Route path="/rentabilidad-sala" element={<ProtectedRoute><Navigate to="/client-profitability-analysis" replace /></ProtectedRoute>} />
          {/* Análisis Financiero — hidden from sidebar but page preserved */}
          <Route path="/financial-analysis-dashboard" element={<ProtectedRoute><FinancialAnalysisDashboard /></ProtectedRoute>} />
          {/* Calendario de Servicios — inside Sala de Corte */}
          <Route path="/calendario-eventos" element={<ProtectedRoute><CalendarioEventos /></ProtectedRoute>} />
          <Route path="/trazabilidad" element={<ProtectedRoute><Trazabilidad /></ProtectedRoute>} />

          {/* ── Clientes Unificados ── */}
          <Route path="/clientes" element={<ProtectedRoute><ClientesUnificados /></ProtectedRoute>} />
          <Route path="/clientes/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />

          {/* ── 3. TIENDA (simplified as operational channel) ── */}
          <Route path="/tienda-general" element={<ProtectedRoute><TiendaGeneral /></ProtectedRoute>} />
          <Route path="/tienda-ventas" element={<ProtectedRoute><TiendaVentas /></ProtectedRoute>} />
          <Route path="/tienda-caja-diaria" element={<ProtectedRoute><TiendaCajaDiaria /></ProtectedRoute>} />
          <Route path="/tienda-tpv-fisico" element={<ProtectedRoute><Navigate to="/tienda-ventas" replace /></ProtectedRoute>} />
          <Route path="/tienda-tpv-online" element={<ProtectedRoute><TiendaGeneral /></ProtectedRoute>} />
          <Route path="/pedidos" element={<ProtectedRoute><PedidosDashboard /></ProtectedRoute>} />
          <Route path="/pedidos/todos" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/pedidos/nuevo" element={<ProtectedRoute><NuevoPedido /></ProtectedRoute>} />
          <Route path="/pedidos/:id" element={<ProtectedRoute><PedidoDetalle /></ProtectedRoute>} />
          <Route path="/pedidos/tienda" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/pedidos/distribucion" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/pedidos/online" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/pedidos/canales" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/pedidos/preparacion" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/pedidos/reparto" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/pedidos/incidencias" element={<ProtectedRoute><PedidosIncidencias /></ProtectedRoute>} />
          <Route path="/pedidos/historico" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/tienda-pedidos-web" element={<ProtectedRoute><Navigate to="/pedidos" replace /></ProtectedRoute>} />
          <Route path="/tienda-analitica" element={<ProtectedRoute><TiendaAnalitica /></ProtectedRoute>} />
          <Route path="/tienda-appcc" element={<ProtectedRoute><APPCCAutocontrol /></ProtectedRoute>} />
          {/* Hidden from Tienda sidebar but pages preserved */}
          <Route path="/tienda-inventario" element={<ProtectedRoute><TiendaInventario /></ProtectedRoute>} />
          <Route path="/tienda-productos" element={<ProtectedRoute><TiendaProductos /></ProtectedRoute>} />
          <Route path="/tienda-movimientos" element={<ProtectedRoute><TiendaMovimientos /></ProtectedRoute>} />
          <Route path="/tienda-proveedores" element={<ProtectedRoute><TiendaProveedores /></ProtectedRoute>} />
          <Route path="/tienda-compras" element={<ProtectedRoute><TiendaCompras /></ProtectedRoute>} />
          <Route path="/tienda-facturas-entrantes" element={<ProtectedRoute><TiendaCompras /></ProtectedRoute>} />
          <Route path="/tienda-dashboard-financiero" element={<ProtectedRoute><TiendaDashboardFinanciero /></ProtectedRoute>} />
          <Route path="/tienda-finanzas" element={<ProtectedRoute><TiendaFinanzas /></ProtectedRoute>} />
          <Route path="/tienda-contabilidad" element={<ProtectedRoute><TiendaGeneral /></ProtectedRoute>} />
          {/* Legacy redirects */}
          <Route path="/erp-tienda-fisica" element={<ProtectedRoute><Navigate to="/tienda-general" replace /></ProtectedRoute>} />
          <Route path="/erp-tienda-online" element={<ProtectedRoute><Navigate to="/tienda-general" replace /></ProtectedRoute>} />

          {/* ── 4. DISTRIBUCIÓN (simplified as operational channel) ── */}
          <Route path="/distribucion" element={<ProtectedRoute><Distribucion /></ProtectedRoute>} />
          <Route path="/dist-clientes" element={<ProtectedRoute><DistClientes /></ProtectedRoute>} />
          <Route path="/dist-clientes/:id" element={<ProtectedRoute><ClienteB2B /></ProtectedRoute>} />
          <Route path="/dist-tarifas" element={<ProtectedRoute><DistTarifas /></ProtectedRoute>} />
          <Route path="/dist-documentos" element={<ProtectedRoute><DistDocumentos /></ProtectedRoute>} />
          <Route path="/dist-productos" element={<ProtectedRoute><DistProductos /></ProtectedRoute>} />
          <Route path="/dist-inventario-b2b" element={<ProtectedRoute><DistInventarioB2B /></ProtectedRoute>} />
          <Route path="/dist-pedidos" element={<ProtectedRoute><Navigate to="/pedidos?area=distribucion" replace /></ProtectedRoute>} />
          <Route path="/dist-pedidos/nuevo" element={<ProtectedRoute><DistNuevoPedido /></ProtectedRoute>} />
          {/* Preparación de Pedidos / Rutas — alias to dist-pedidos until dedicated page exists */}
          <Route path="/dist-preparacion" element={<ProtectedRoute><Navigate to="/pedidos/preparacion" replace /></ProtectedRoute>} />
          <Route path="/dist-rutas" element={<ProtectedRoute><Navigate to="/pedidos/reparto" replace /></ProtectedRoute>} />
          <Route path="/dist-analitica" element={<ProtectedRoute><DistAnalitica /></ProtectedRoute>} />
          {/* Hidden from Distribución sidebar but pages preserved */}
          <Route path="/dist-inventario" element={<ProtectedRoute><DistInventario /></ProtectedRoute>} />
          <Route path="/dist-facturas-entrantes" element={<ProtectedRoute><DistFacturasEntrantes /></ProtectedRoute>} />
          <Route path="/dist-facturas-salientes" element={<ProtectedRoute><DistFacturasSalientes /></ProtectedRoute>} />
          <Route path="/dist-cobros" element={<ProtectedRoute><Distribucion /></ProtectedRoute>} />
          <Route path="/dist-pagos" element={<ProtectedRoute><Distribucion /></ProtectedRoute>} />
          <Route path="/dist-contabilidad" element={<ProtectedRoute><Distribucion /></ProtectedRoute>} />
          <Route path="/dist-finanzas" element={<ProtectedRoute><Distribucion /></ProtectedRoute>} />
          {/* Legacy redirect */}
          <Route path="/erp-distribucion" element={<ProtectedRoute><Navigate to="/distribucion" replace /></ProtectedRoute>} />

          {/* ── 5. PRODUCTOS Y ALMACÉN (corporate catalog — reuses tienda pages) ── */}
          {/* Routes already defined above under Tienda; accessible from this section too */}

          {/* ── 6. COMPRAS Y FACTURACIÓN ── */}
          {/* Compras — reuses tienda-compras (OCR, facturas, proveedores, inventario intact) */}
          <Route path="/compras-importar" element={<ProtectedRoute><TiendaCompras /></ProtectedRoute>} />
          <Route path="/compras-bandeja-email" element={<ProtectedRoute><TiendaCompras /></ProtectedRoute>} />
          {/* Facturación */}
          <Route path="/fin-facturacion" element={<ProtectedRoute><FinFacturacion /></ProtectedRoute>} />
          <Route path="/fin-albaranes" element={<ProtectedRoute><FinAlbaranes /></ProtectedRoute>} />

          {/* ── 7. FINANZAS ── */}
          <Route path="/finanzas-corporativas" element={<ProtectedRoute><FinanzasCorporativas /></ProtectedRoute>} />
          <Route path="/fin-contabilidad" element={<ProtectedRoute><FinContabilidad /></ProtectedRoute>} />
          <Route path="/fin-tesoreria" element={<ProtectedRoute><FinTesoreria /></ProtectedRoute>} />
          <Route path="/fin-bancos" element={<ProtectedRoute><FinBancos /></ProtectedRoute>} />
          <Route path="/fin-gastos-fijos" element={<ProtectedRoute><FinGastos /></ProtectedRoute>} />
          <Route path="/fin-gastos-variables" element={<ProtectedRoute><FinGastos /></ProtectedRoute>} />
          <Route path="/fin-gastos" element={<ProtectedRoute><FinGastos /></ProtectedRoute>} />
          <Route path="/fin-configuracion-empresa" element={<ProtectedRoute><FinConfiguracionEmpresa /></ProtectedRoute>} />
          <Route path="/fin-ocr" element={<ProtectedRoute><FinanzasCorporativas /></ProtectedRoute>} />
          <Route path="/fin-centros-coste" element={<ProtectedRoute><FinanzasCorporativas /></ProtectedRoute>} />
          {/* Auditoría */}
          <Route path="/auditoria-total" element={<ProtectedRoute><AuditoriaTotal /></ProtectedRoute>} />
          {/* Legacy redirect */}
          <Route path="/erp-finanzas" element={<ProtectedRoute><Navigate to="/finanzas-corporativas" replace /></ProtectedRoute>} />

          {/* ── 8. IA Empresarial ── */}
          <Route path="/ia-empresarial" element={<ProtectedRoute><IAEmpresarial /></ProtectedRoute>} />
          {/* Legacy redirect */}
          <Route path="/erp-ia-empresarial" element={<ProtectedRoute><Navigate to="/ia-empresarial" replace /></ProtectedRoute>} />

          {/* ── Inventario: Costes y Pricing (legacy, sin acceso desde menú) ── */}
          <Route path="/costes-pricing" element={<ProtectedRoute><CostesPricing /></ProtectedRoute>} />
          <Route path="/lector-precios" element={<ProtectedRoute><LectorPrecios /></ProtectedRoute>} />
          <Route path="/precios" element={<ProtectedRoute><GestionPrecios /></ProtectedRoute>} />

          {/* ── Legacy ERP stubs (kept) ── */}
          <Route path="/erp-inventario" element={<ProtectedRoute><ERPInventario /></ProtectedRoute>} />
          <Route path="/erp-comercial" element={<ProtectedRoute><ERPComercial /></ProtectedRoute>} />

          {/* ── COMERCIAL ── */}
          <Route path="/comercial-dashboard" element={<ProtectedRoute><ComercialDashboard /></ProtectedRoute>} />
          <Route path="/comercial-clientes" element={<ProtectedRoute><ComercialClientes /></ProtectedRoute>} />
          <Route path="/comercial-clientes/:id" element={<ProtectedRoute><ComercialClienteFicha /></ProtectedRoute>} />
          <Route path="/comercial-comunicaciones" element={<ProtectedRoute><ComercialComunicaciones /></ProtectedRoute>} />
          <Route path="/comercial-atencion" element={<ProtectedRoute><ComercialAtencion /></ProtectedRoute>} />
          <Route path="/comercial-campanas" element={<ProtectedRoute><ComercialCampanas /></ProtectedRoute>} />
          <Route path="/comercial-marketing" element={<ProtectedRoute><ComercialMarketing /></ProtectedRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
